/*
 * Vieux (spec 2026-10-04 §4.1; approved prototype CP4 2026-10-16): an archived colour never stays in the
 * bag. Archived colours have 0 stock and no overselling (ruling D3), so Shopify refuses the add itself;
 * this is defence in depth for anything that still reaches the cart (a colour flagged later while it
 * has stock). Any row the cart renders with [data-vieux-archived-line] (snippets/cart-products.liquid) is
 * set to quantity 0 through /cart/change.js and the page reloads; the reloaded cart then says, one
 * sentence per line, "St Jean in Lemon Mocha is archived, so we took it out of your cart."
 *
 * The sentences never travel in the URL: the reload carries only a random token (?vx_archived=), and
 * the sentences are kept in sessionStorage under that token (top window only, so no iframe copy of
 * this script can touch them). A missing token or entry prints nothing. The attempt count travels in
 * the URL (?vx_archived_try=), not storage, so a removal that fails is retried once at most and a
 * throwing sessionStorage can never cause a reload loop; after that the row shows its own
 * "can't be bought" note. Checkout, including the express checkout buttons, is inert while a removal
 * is pending or has failed. Runs once.
 */
if (!window.__vieuxCartGuard && window.top === window) {
  window.__vieuxCartGuard = true;
  const TOKEN = 'vx_archived';
  const TRY = 'vx_archived_try';
  const KEY = (token) => `vxArchived:${token}`;

  const disableCheckout = () => {
    document.querySelectorAll('[name="checkout"], a[href*="/checkout"]').forEach((el) => el.setAttribute('aria-disabled', 'true'));
    document
      .querySelectorAll('.additional-checkout-buttons, .cart__additional-checkout-buttons, shopify-accelerated-checkout-cart')
      .forEach((el) => {
        el.setAttribute('inert', '');
        el.setAttribute('aria-disabled', 'true');
      });
  };

  // Read and strip this load's parameters once.
  const url = new URL(location.href);
  const token = url.searchParams.get(TOKEN) || '';
  const tries = Number.parseInt(url.searchParams.get(TRY) || '0', 10) || 0;
  if (url.searchParams.has(TOKEN) || url.searchParams.has(TRY)) {
    url.searchParams.delete(TOKEN);
    url.searchParams.delete(TRY);
    history.replaceState(history.state, '', url.toString());
  }

  /** @type {string[]} */
  const notes = (() => {
    if (!/^[a-z0-9]{8,40}$/.test(token)) return [];
    try {
      const raw = sessionStorage.getItem(KEY(token));
      sessionStorage.removeItem(KEY(token));
      const list = JSON.parse(raw || 'null');
      return Array.isArray(list) ? list.map(String).filter(Boolean) : [];
    } catch {
      return [];
    }
  })();

  const showNotes = () => {
    if (!notes.length) return;
    document.querySelectorAll('.cart-items__wrapper').forEach((list) => {
      if (list.querySelector('.vx-cart-notice')) return;
      notes
        .slice()
        .reverse()
        .forEach((text) => {
          const p = document.createElement('p');
          p.className = 'vx-cart-notice';
          p.setAttribute('role', 'status');
          p.textContent = text;
          list.prepend(p);
        });
    });
  };

  const reloadWith = (params) => {
    const next = new URL(location.href);
    for (const [k, v] of Object.entries(params)) next.searchParams.set(k, v);
    location.replace(next.toString());
  };

  let busy = false;
  const sweep = async () => {
    const rows = [...document.querySelectorAll('[data-vieux-archived-line]')];
    // "We took it out" only when nothing archived is left; otherwise the row's own note speaks.
    if (!rows.length) {
      showNotes();
      return;
    }
    disableCheckout();
    if (busy) return;
    if (tries >= 1) {
      // One failed attempt already: stop, and say so on the row.
      rows.forEach((r) => r.classList.add('vx-archived-failed'));
      return;
    }
    busy = true;
    const keys = [...new Set(rows.map((r) => r.getAttribute('data-vieux-archived-line') || ''))].filter(Boolean);
    const removed = [...new Set(rows.map((r) => r.getAttribute('data-vieux-archived-note') || '').filter(Boolean))];
    try {
      for (const key of keys) {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        if (!res.ok) throw new Error(`cart/change ${res.status}`);
      }
    } catch (error) {
      console.error('vieux-cart-guard', error);
      reloadWith({ [TRY]: String(tries + 1) });
      return;
    }
    const next = Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => (b % 36).toString(36)).join('');
    try {
      sessionStorage.setItem(KEY(next), JSON.stringify(removed));
      // The attempt count rides along: if change.js said 200 but the line is still there, the
      // reloaded page stops (tries >= 1) instead of looping.
      reloadWith({ [TOKEN]: next, [TRY]: String(tries + 1) });
    } catch {
      reloadWith({ [TRY]: String(tries + 1) }); // storage off: no notice, same once-only stop
    }
  };

  const start = () => {
    sweep();
    new MutationObserver(() => sweep()).observe(document.documentElement, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}
